<template>
  <tx-dialog
    v-model="visible" :title="t('customSort.title')" width="40%" height="65%" :loading="loading" show-ok-cancel @ok="confirm"
    @closed="close" @opened="opened" @click="cancel"
  >
    <div class="w-full h-full">
      <div class="w-full my-2">
        <tx-select
          v-model="sortProperty" :label="t('customSort.selectArticleProperty')" :data="sortableProperties" value-prop="articleProperty" display-prop="displayLabel"
          clearable filterable @change="onSortPropertySelectionChange"
        />
      </div>
      <div class="w-full my-2">
        <tx-list-transfer
          v-model:selected-items="sortPropertySelectedValues" class="max-h-96" :items="sortPropertyValues"
          :available-items-label="t('customSort.available')" :selected-items-label="t('customSort.sorted')"
          item-key-prop="index" item-display-prop="value"
        />
      </div>
    </div>
  </tx-dialog>
</template>

<script setup lang='ts'>
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import TxDialog from '@/shared/components/TxDialog.vue'
import { useUserStore } from '@/store/userData'
import type { AttributeType } from '@/models/catalogAttribute'
import TxListTransfer from '@/shared/components/txListTransfer'
import TxSelect from '@/shared/components/TxSelect.vue'
import utils from '@/services/utils'
import { getAttributeUniqueValues } from '@/api/t1/article'

interface IProps {
  modelValue: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
})

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'customSort', selectedArticleSortPropertyObject: Record<string, string[]>): void
  (e: 'update:modelValue', modelValue: boolean): void
}>()

const { t } = useI18n()
const userStore = useUserStore()

const visible = ref(false)
const loading = ref(false)
const sortProperty = ref<string>('')
const sortPropertyValues = ref<any[]>([])
const sortPropertySelectedValues = ref<any[]>([])

const sortableProperties = computed(() => {
  const sortableProperties = [] as Array<{ displayLabel: string, articleProperty: string, direction?: string, typeId: AttributeType }>
  userStore.activeCatalog?.Config.ArticlesSortProperties.forEach((articleSortConfigValue) => {
    if (userStore.myAttributes && userStore.myAttributes.hasOwnProperty(articleSortConfigValue.articleProperty)) {
      const sortOption = {
        displayLabel: userStore.myAttributes[articleSortConfigValue.articleProperty].DisplayName,
        articleProperty: articleSortConfigValue.articleProperty,
        direction: articleSortConfigValue.sortDirection,
        typeId: userStore.myAttributes[articleSortConfigValue.articleProperty].AttributeType,
      }
      utils.insertSorted(sortOption, sortableProperties, (a, b) => utils.comparer(a, b, ['displayLabel']))
    }
  })
  return sortableProperties
})

function onSortPropertySelectionChange(articleProperty: string) {
  sortPropertyValues.value = []
  if (userStore.activeCatalog) {
    loading.value = true
    let attributeSystemName = articleProperty
    if (articleProperty === '_WholesalePrice' && userStore.priceGroups.wholesale) {
      attributeSystemName = userStore.priceGroups.wholesale.Id.toString()
    }
    else if (articleProperty === '_RetailPrice' && userStore.priceGroups.retail) {
      attributeSystemName = userStore.priceGroups.retail.Id.toString()
    }
    else if (articleProperty === '_OutletPrice' && userStore.priceGroups.outlet) {
      attributeSystemName = userStore.priceGroups.outlet.Id.toString()
    }
    getAttributeUniqueValues(userStore.activeCatalog.CatalogCode, attributeSystemName)
      .then((values) => {
        sortPropertyValues.value = values.map((value, index) => ({ index, value }))
      })
      .finally(() => {
        loading.value = false
      })
  }
  sortPropertySelectedValues.value = []
}

async function confirm() {
  loading.value = true
  const selectedArticleSortPropertyObject: Record<string, string[]> = {}
  sortPropertySelectedValues.value.forEach((itm) => {
    if (!selectedArticleSortPropertyObject[sortProperty.value]) {
      selectedArticleSortPropertyObject[sortProperty.value] = []
    }
    selectedArticleSortPropertyObject[sortProperty.value].push(itm.value)
  })
  emit('customSort', selectedArticleSortPropertyObject)
}

function cancel() {
  close()
  emit('cancel')
}

function close() {
  emit('update:modelValue', false)
}

function opened() {
  loading.value = false
}

watch(() => userStore.sortBy, (sortByN, sortByO) => {
  sortProperty.value = sortByO == null || sortByO === '_customSort' ? '' : sortByO
  if (sortProperty.value !== '') { // avoid default sort
    onSortPropertySelectionChange(sortProperty.value)
  }
})

watch(() => props.modelValue, (modelValue) => {
  visible.value = modelValue
})
</script>
